import { SPEED_OF_SOUND } from './constants';

export function calculateModes(Lx, Ly, Lz, maxFreq = 500) {
  const modes = [];
  const half = SPEED_OF_SOUND / 2;
  const maxN = 20;
  for (let nx = 0; nx <= maxN; nx++) {
    const fx = nx / Lx;
    // minimum possible f for this nx (at ny=nz=0) already exceeds limit
    if (nx > 0 && half * fx > maxFreq) break;
    for (let ny = 0; ny <= maxN; ny++) {
      const fy = ny / Ly;
      // minimum possible f for this (nx,ny) pair (at nz=0) already exceeds limit
      if (half * Math.sqrt(fx * fx + fy * fy) > maxFreq) break;
      for (let nz = 0; nz <= maxN; nz++) {
        if (nx === 0 && ny === 0 && nz === 0) continue;
        const fz = nz / Lz;
        const f = half * Math.sqrt(fx * fx + fy * fy + fz * fz);
        if (f > maxFreq) break; // f is monotonically increasing with nz
        const nonZero = (nx > 0 ? 1 : 0) + (ny > 0 ? 1 : 0) + (nz > 0 ? 1 : 0);
        let type;
        if (nonZero === 1) type = "axial";
        else if (nonZero === 2) type = "tangential";
        else type = "oblique";
        modes.push({ nx, ny, nz, freq: f, type });
      }
    }
  }
  modes.sort((a, b) => a.freq - b.freq);
  return modes;
}

export function getBoltArea(Lx, Ly, Lz) {
  const V = Lx * Ly * Lz;
  const S = 2 * (Lx * Ly + Ly * Lz + Lx * Lz);
  const E = 4 * (Lx + Ly + Lz);
  return { V, S, E };
}

export function modeDensityPerBand(modes, bandwidth = 10, maxFreq = 500) {
  const numBands = Math.floor(maxFreq / bandwidth);
  const counts = new Int32Array(numBands);
  for (const m of modes) {
    const i = Math.floor(m.freq / bandwidth);
    if (i < numBands) counts[i]++;
  }
  const bands = [];
  for (let i = 0; i < numBands; i++) {
    bands.push({ center: i * bandwidth + bandwidth / 2, count: counts[i] });
  }
  return bands;
}

export function modeDescription({ nx, ny, nz, type }) {
  if (type === 'axial') {
    const n = Math.max(nx, ny, nz);
    const axis = nx > 0 ? 'length' : ny > 0 ? 'width' : 'height';
    const ordinal = n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;
    return `${ordinal} harmonic along the room's ${axis}. Strong resonance between the two ${axis} walls — likely audible as bass buildup at this frequency.`;
  }
  if (type === 'tangential') {
    const axes = [nx > 0 ? 'length' : null, ny > 0 ? 'width' : null, nz > 0 ? 'height' : null].filter(Boolean);
    return `Diagonal resonance coupling the ${axes[0]} and ${axes[1]}. About half the energy of an axial mode — can cause uneven bass at certain listening positions.`;
  }
  return `Three-dimensional resonance involving all six walls. Lower energy than axial or tangential modes — less likely to cause audible coloration at a single spot.`;
}
